import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  Signal,
} from '@angular/core';
import { ApiHttpResponse, GpsOnly, IconType } from '@kinomap-dev/ng-ui-lib/core';
import { EmptyContentListElement } from '../../../components/empty-content-list-element/empty-content-list-element';
import { Paginator } from '../../../components/paginator/paginator';
import { Table } from '../../../components/table/table';
import { ProfileService } from '../../../services/profile.service';
import { TableContentService, CONTENT_TYPE_TOKEN } from '../../../services/table-content.service';
import { convertGpsTrackToGpsOnlyContentTableItem } from '../../../utils/gps-track-to-gps-only-content-table-data.helper';
import { FilterService } from '../../../services/filters.service';
import { TableColumnsService } from '../../../services/table-columns.service';

const tableHeaderElements: string[] = ['sport', 'level', 'sessions', 'comments', 'likes']; // 'labels' hidden while the tags topic is on standby

@Component({
  selector: 'app-gps-only-list',
  imports: [NgTemplateOutlet, EmptyContentListElement, Table, Paginator],
  templateUrl: './gps-only-list.html',
  styleUrl: '../content-list.scss',
  providers: [
    FilterService,
    TableContentService,
    TableColumnsService,
    { provide: CONTENT_TYPE_TOKEN, useValue: 'gps-only' },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GpsOnlyList {
  public gpsOnlyTableContentService: TableContentService = inject(TableContentService);
  readonly tableColumnsService: TableColumnsService = inject(TableColumnsService);
  profileService: ProfileService = inject(ProfileService);

  readonly emptyList: Signal<boolean> = computed(
    () => (this.profileService.profile()?.gps_only_count ?? 0) === 0,
  );
  readonly actions: Signal<IconType[]> = signal<IconType[]>(['edit', 'options']);
  readonly gpsOnlyTableData: Signal<ApiHttpResponse<GpsOnly[]> | undefined> = this
    .gpsOnlyTableContentService.tableData as Signal<ApiHttpResponse<GpsOnly[]>>;

  private profileGpsOnlyCount = computed(() => this.profileService.profile()?.gps_only_count ?? 0);
  readonly skeletonCount: Signal<number> = computed(() => {
    if (this.profileGpsOnlyCount() < this.gpsOnlyTableContentService.contentLimitPerPage()) {
      return this.profileGpsOnlyCount();
    }
    return this.gpsOnlyTableContentService.contentLimitPerPage();
  });

  readonly tableData: Signal<any[] | undefined> = computed(() => {
    const data: GpsOnly[] | undefined = this.gpsOnlyTableData()?.data;
    if (!data || !Array.isArray(data)) return undefined;
    return data.map((row: GpsOnly) => {
      return convertGpsTrackToGpsOnlyContentTableItem(row);
    });
  });

  constructor() {
    this.tableColumnsService.setColumns(tableHeaderElements);
  }
}
